import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, homePath } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { WIBClock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const HERO = "/rscm.jpg";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sesiBerakhir = params.get("sesi") === "berakhir";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(homePath(user));
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username, password);
      navigate(homePath(u));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Gagal masuk");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Rumah Sakit" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-deep/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo-simutu.png" alt="SIMUTU" className="w-11 h-11 rounded-xl object-contain" />
            <span className="font-display font-extrabold text-xl">SIMUTU Rawat Jalan</span>
          </div>
          <div className="max-w-md">
            <h2 className="text-3xl font-extrabold font-display leading-tight">
              Monitoring Capaian Indikator Mutu Pelayanan Rawat Jalan
            </h2>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo-simutu.png" alt="SIMUTU" className="w-10 h-10 rounded-lg object-contain" />
            <span className="font-display font-extrabold text-xl text-slate-900">SIMUTU</span>
          </div>
          <WIBClock className="mb-5" />
          <h1 className="text-3xl font-extrabold font-display text-slate-900">Masuk</h1>
          <p className="text-slate-500 mt-2 mb-8 text-sm">Silakan masuk untuk mengelola data mutu.</p>

          {sesiBerakhir && !error && (
            <div
              data-testid="login-session-expired"
              className="mb-5 text-sm text-brand-deep bg-brand-mist border border-brand-sky/60 rounded-md px-3 py-2"
            >
              Sesi berakhir, silakan masuk kembali
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Masukkan username"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 text-base"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Masuk
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
